/**
 * Everflow affiliate tracking — shared constants.
 *
 * Everflow records the click itself (redirect tracking links) and hands the
 * visitor to our landing page with `_ef_transaction_id` in the URL; for direct
 * links our middleware records the click via `/clk`. Our job is to keep that id
 * with the visitor across all of our registrable domains and to report funnel
 * events / the registration conversion back with server-to-server postbacks.
 * See `plans/everflow-tracking.md` and `docs/everflow-tracking.md`.
 */

/** Our advertiser id in Everflow. */
export const EVERFLOW_ADVERTISER_ID = 1;

/**
 * Our Everflow tracking domain: partner links, `/clk` and the advertiser postback all live here.
 * Moved from `www.pqanr7dftrk.com` (browsers flagged it as malware). `/clk` confirmed live on this
 * host on 2026-09-24 (test partner 5, offer 851: `error_code: 0`, 32-hex `transaction_id`,
 * `session_duration: 720`, `aid: 1`). Everflow confirmed on 2026-09-25 that the advertiser postback
 * goes to this host as well (`https://www.mypickleballtournaments.com/?nid=3004&…`) and that it is
 * the default tracking domain for partner links from now on; the old domain still redirects.
 */
export const EVERFLOW_TRACKING_DOMAIN = 'www.mypickleballtournaments.com';

/**
 * Query parameter that carries the Everflow transaction id between domains.
 * Produced by Everflow's Destination URL macro and read by pickleballbrackets.com
 * (checkout v1) — never rename it.
 */
export const EF_TID_PARAM = '_ef_transaction_id';

/**
 * Offer and affiliate of the click, appended to every destination URL by Everflow's `{offer_id}` /
 * `{affiliate_id}` macros (live on QA offer 853 since 2026-10-16, rollout to the other offers pending).
 * The sub ids and `source_id` arrive under their plain Everflow names (`sub1`…`sub5`, `source_id`).
 */
export const EF_OFFER_PARAM = '_ef_oid';
export const EF_AFFILIATE_PARAM = '_ef_affid';

/**
 * Our first-party cookie holding the current transaction id. Set on the
 * registrable domain (e.g. `.pickleballtournaments.com`) so every subdomain,
 * checkout included, can read it.
 */
export const EF_TID_COOKIE = 'pb_ef_tid';

/**
 * Our per-tournament click cookie — the one the funnel events and the checkout conversion read.
 * One entry per tournament, `<tournament uuid>~<transaction id>~<offer id>~<affiliate id>~<sent event
 * ids>~<click time>`, entries joined with `|`, newest first, at most `EF_TX_MAX_ENTRIES` (≈ 95 bytes each,
 * well under the 4 KB cookie limit). A click for tournament A never counts for tournament B (one offer
 * per tournament); a new click for the same tournament replaces its entry and clears the sent flags.
 * `HttpOnly` — only middleware and server actions read it. Same domain and lifetime as `pb_ef_tid`.
 */
export const EF_TX_COOKIE = 'pb_ef_tx';
export const EF_TX_MAX_ENTRIES = 10;
/** Entry key while the landing slug is not resolved to the tournament uuid yet (BE slow/down). */
export const EF_TX_SLUG_KEY_PREFIX = 'slug:';

/**
 * Cookie lifetime for redirect-link landings (direct-link landings take `session_duration` from
 * `/clk`). This is the click's attribution session: offer 851 answers 720 h = 30 days (2026-09-24),
 * and 30 days is also Everflow's first-party cookie default (2026-09-25). Not to be confused with the
 * optional per-offer "Click-to-Conversion Time" cutoff, which Everflow applies on their side and we
 * cannot influence. Whether every offer uses 720 h is still asked — if one is longer, raise this.
 */
export const EF_DEFAULT_CLICK_WINDOW_DAYS = 30;

/** Everflow transaction ids are 32 lowercase hex characters. */
export const EF_TID_PATTERN = /^[a-f0-9]{32}$/i;

/**
 * Our Everflow network id — the `nid` on the advertiser postback. Confirmed by Everflow on
 * 2026-09-19: **3004**. `null` keeps every postback (funnel events + base conversion) switched off.
 *
 * Set to 3004 on this branch on 2026-09-24 for the QA on the PR preview (DEV data, Stripe test
 * mode, Everflow's TestAccount partner) and kept: the branch merges as part of the PROD release that
 * ships together with the BE side (#283) — decided 2026-10-21. Postbacks on PROD start with that
 * release; the DB contract is gated separately by `EVERFLOW_BE_CONTRACT_ENVS`.
 */
export const EVERFLOW_NETWORK_ID: number | null = 3004;

/**
 * Path of the advertiser postback on the tracking domain. Everflow's example for our account
 * (2026-09-19) posts to the domain root: `https://<tracking domain>/?nid=3004&transaction_id=…`.
 */
export const EF_POSTBACK_PATH = '/';

/**
 * Advertiser brand-level event ids (`adv_event_id` on the postback), created by Everflow on
 * 2026-09-23 under the United Pickleball Association brand: 2 = "Tournament Details View",
 * 3 = "Registration Started". `null` would switch an event off; nothing fires anyway while
 * `EVERFLOW_NETWORK_ID` is `null`. As of 2026-09-24 both events are tied to 0 offers — Everflow
 * has to associate them with every offer (new offers: "Tie with global brand event") before the
 * postbacks go live, or the event postbacks are rejected. TODO(PW-7720): confirm the association.
 */
export const EVERFLOW_EVENTS: Record<
  'DETAILS_VIEW' | 'REGISTRATION_STARTED' | 'REGISTRATION_EDITED' | 'CHECKOUT_COMPLETED',
  number | null
> = {
  DETAILS_VIEW: 2,
  REGISTRATION_STARTED: 3,
  /**
   * Created by Everflow on 2026-10-16 (QA offer 853 first, all active offers after the 853 run):
   * 5 = "Registration Edited" (duplicates allowed — a player edits several times),
   * 6 = "Checkout Completed" (duplicates rejected — the first checkout of a click only).
   * The Base conversion (no event id) is "Payment Received": duplicates allowed, the only payable one.
   */
  REGISTRATION_EDITED: 5,
  CHECKOUT_COMPLETED: 6,
};

/**
 * Custom postback parameter the per-offer postback controls match on (`tournamentid = <tournament
 * GUID>`, case-insensitive on the name). Not a report column — `adv1` carries the tournament uuid for
 * reports. Kept until Everflow has removed the controls (agreed 2026-10-19), then it can go.
 */
export const EF_TOURNAMENT_POSTBACK_PARAM = 'tournamentid';

/**
 * Postbacks run in the background (`waitUntil` / `after()`), so a generous timeout costs nothing.
 * The preview QA (2026-09-25) showed Everflow taking well over 3 s at times and still recording the
 * conversion after our fetch had given up — a short timeout only produces false "failed" log lines.
 * Never retry a failed postback for the same reason: the first attempt may have been recorded.
 */
export const EF_POSTBACK_TIMEOUT_MS = 10000;

/**
 * Wait before sending a funnel event so Everflow already knows the click. A postback that arrives
 * before the click is queryable is answered 2xx and dropped silently (preview QA, 2026-09-25: an
 * event sent 0.3 s after the redirect landing vanished; one sent 1.5 s later was recorded). The
 * event runs after the response, so the visitor never waits for this.
 */
export const EF_EVENT_POSTBACK_DELAY_MS = 2500;

/**
 * The once-guard cookies of the first implementation (`pb_ef_ev_<event id>`), replaced by the sent flags
 * inside `pb_ef_tx`. Still in browsers for up to 30 days — expired on sight.
 */
export const EF_LEGACY_EVENT_COOKIE_PREFIX = 'pb_ef_ev_';

/**
 * Reg's BE contract for the click rows and the lock (doc "Everflow (PW-7720) — PBB changes for the FE",
 * 2026-10-14; built as pickleball-brackets#283): the proc `API_v2_Everflow_SaveClick(UserID, ModuleID,
 * Module, EverflowTransactionID, ClickData)` → `EverflowTransactionID` (the id that counts) + `IsLocked`,
 * the new `GetCheckout` columns (`AttendeeHeaderID`, `IsExistingRegistration`, `CartChanged`,
 * `EverflowTransactionID`, `EverflowClickData`) and two new `SavePayment` parameters.
 *
 * On per environment (`NEXT_PUBLIC_APP_ENV`): only where the PBB tables + procs *and* the pickleball-api
 * whitelist for the click proc are deployed — Go binds every body key to a proc parameter, so an unknown
 * `SavePayment` key fails the save *after* the card was charged. `dev` since 2026-10-20 (Reg + Denis);
 * `test` and `prod` since the PROD release of 2026-10-26, deployed together with #283 (whitelist on
 * dev/test/prod since 2026-10-20). Off = Phase 1 behaviour (cookie only, attendee only).
 */
export const EVERFLOW_BE_CONTRACT_ENVS: readonly string[] = ['dev', 'test', 'prod'];

export const isEverflowBeContractEnabled = (appEnv: string | null | undefined): boolean =>
  !!appEnv && EVERFLOW_BE_CONTRACT_ENVS.includes(appEnv);

export const EF_SAVE_CLICK_SP = 'API_v2_Everflow_SaveClick';
/** `Module` of a tournament click row (leagues and team leagues come later). */
export const EF_MODULE_TOURNAMENT = 'Tourney';
/**
 * The row is written while the visitor waits (the landing's 302, the details page, the checkout's first
 * step) so the cookie can remember that it exists — a failure only means the next page tries again.
 */
export const EF_SAVE_CLICK_TIMEOUT_MS = 1500;
/** Longest sub id / source id kept in `ClickData` — Everflow's own limit is 255 characters. */
export const EF_CLICK_DATA_MAX_VALUE_LENGTH = 255;

/**
 * Partner clicks (Phase 3, decision 20). A player with no click of their own who starts a checkout
 * where a partner holding a locked click asked for them (`GetEvents.Partner_EverflowTransactionID`, or
 * the team captain's on `GetTeamEvents`) gets a click generated on our server: `/clk` on that
 * partner's offer and affiliate with the player's own IP and user agent, marked `sub5=partner:<their
 * transaction id>`, kept like any click (cookie entry flagged `g`, row with `source: generated`). What
 * finalize does with it is `EVERFLOW_PARTNER_CREDIT`.
 */
export const EF_PARTNER_MARKER_PREFIX = 'partner:';
/**
 * Budget for the `/clk` call (and for the rows read, when a caller has no rows) when the click is generated from the
 * select-events step. The player never waits for it (a client effect asks the server after the step rendered), so
 * the budget is generous. The step itself hands the action the id-holder it found on the rows it rendered — no
 * second read: `GetEvents` + `GetTeamEvents` took 3–8+ s on the DEV API during the 21 Oct preview run.
 */
export const EF_PARTNER_CLICK_TIMEOUT_MS = 8000;
/** Generated inline at finalize when the select-events step was skipped (deep link to the payment step). */
export const EF_PARTNER_CLICK_INLINE_TIMEOUT_MS = 3000;

/**
 * Whether a generated partner click is used at the player's first finalize (Q-S10, management pending):
 * - `invitation` (default, Reg's model of 15 Oct): the invitation itself credits the registration — the
 *   id goes into `SavePayment` whatever the finalized cart contains;
 * - `cart`: only if the finalized cart still holds an event the player was pre-selected into by a partner
 *   or a team (`PreSelectedByPartner` / `PreSelectedByTeam` with `IsInCart`); otherwise the registration
 *   is organic and the click stays in Everflow as a non-converting click.
 */
export type EverflowPartnerCredit = 'invitation' | 'cart';
export const EVERFLOW_PARTNER_CREDIT: EverflowPartnerCredit = 'invitation';

/**
 * Everflow's Network API (D7): `GET /networks/reporting/clicks/{transactionId}` returns Everflow's own
 * record of a click — offer, affiliate, `sub1`–`sub10`, `source_id`, time, device — authenticated with a
 * read-only key in the `X-Eflow-Api-Key` header (`EVERFLOW_API_KEY`, created 2026-10-20: Offer and Partner
 * read-only, Reporting Click + Conversion). Used to fill in what a click row would otherwise lack: the subs
 * of a click copied for a partner, and of a click written from the cookie (logged-out landing). Optional:
 * without the key nothing is looked up.
 */
export const EVERFLOW_API_URL = 'https://api.eflow.team/v1';
export const EF_API_KEY_HEADER = 'X-Eflow-Api-Key';
/** The lookup runs in the background (partner clicks) or next to a row write the visitor waits for — keep it short. */
export const EF_API_TIMEOUT_MS = 1500;

/**
 * Slug → tournament uuid lookup at landing (`/tournaments/<slug>`): the entry must be keyed by the
 * uuid (slugs can change, and the checkout and the postbacks only know the uuid). Short timeout — the
 * visitor is waiting; on failure the entry is stored under the slug and resolved on a later request.
 */
export const EF_TOURNAMENT_LOOKUP_TIMEOUT_MS = 1500;
export const EF_TOURNAMENT_LOOKUP_CACHE_TTL_MS = 60 * 60 * 1000;

/**
 * Server-side clicks for direct-link landings (`oid` + `affid` on our URL, no transaction id):
 * middleware records the click with `GET /clk`, forwarding the visitor's IP and user agent —
 * Everflow confirmed (2026-09-23) both are honoured as passed. Switched on 2026-09-24 after `/clk`
 * was confirmed live on the tracking domain (test partner 5, offer 851); `false` switches every
 * `/clk` call off again. Independent of the postbacks, which `EVERFLOW_NETWORK_ID` gates.
 */
export const EVERFLOW_SERVER_SIDE_CLICKS_ENABLED = true;

export const EF_CLICK_PATH = '/clk';
export const EF_CLICK_TIMEOUT_MS = 1500;

/**
 * Landing-URL parameters forwarded to `/clk` when present — Everflow's server-side click API
 * documents `sub1`–`sub10`, `source_id`, `uid`, `creative_id`, `coupon_code`, `fbclid`, `gclid`.
 */
export const EF_CLICK_PARAMS = [
  'oid',
  'affid',
  'sub1',
  'sub2',
  'sub3',
  'sub4',
  'sub5',
  'sub6',
  'sub7',
  'sub8',
  'sub9',
  'sub10',
  'source_id',
  'uid',
  'creative_id',
  'coupon_code',
  'fbclid',
  'gclid',
] as const;

/**
 * Everflow-only parameters removed from the landing URL by the clean-URL redirect — after a
 * server-side click (direct link) and after a redirect-link landing alike — so a reload, bookmark or
 * shared URL does not record a second click or hand the id to someone else. `fbclid` / `gclid` stay:
 * other tools read them.
 */
export const EF_CLICK_STRIP_PARAMS = [
  'oid',
  'affid',
  EF_OFFER_PARAM,
  EF_AFFILIATE_PARAM,
  'sub1',
  'sub2',
  'sub3',
  'sub4',
  'sub5',
  'sub6',
  'sub7',
  'sub8',
  'sub9',
  'sub10',
  'source_id',
  'uid',
  'creative_id',
  'coupon_code',
  EF_TID_PARAM,
] as const;

/**
 * Remembers the last direct link recorded (`<oid>:<affid>`) for a short while, so back/forward
 * navigation to the original URL or a reload before the redirect settles is not a second click.
 */
export const EF_LAST_CLICK_COOKIE = 'pb_ef_last';
export const EF_LAST_CLICK_MAX_AGE = 30 * 60;
